import React, { useEffect, useRef, useState } from 'react';
import { simulateThrow, sampleThrow, restQuat, qCss, DIE_RATIO } from './diceSim';
import { sfx } from './rtbAudio';

export const diceBus = { throw: null };

const PIPS = { 1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8] };
const FACE_POS = { 1: 'translateZ(var(--h))', 6: 'rotateY(180deg) translateZ(var(--h))', 2: 'rotateY(90deg) translateZ(var(--h))', 5: 'rotateY(-90deg) translateZ(var(--h))', 3: 'rotateX(90deg) translateZ(var(--h))', 4: 'rotateX(-90deg) translateZ(var(--h))' };

function Face({ n }) {
  return (
    <div className="rtb-face" style={{ transform: FACE_POS[n] }}>
      {Array.from({ length: 9 }, (_, i) => <span key={i} className={PIPS[n].includes(i) ? 'rtb-pip' : ''} />)}
    </div>
  );
}

function useThrowEngine(size, value) {
  const dieRef = useRef(null);
  const cubeRef = useRef(null);
  const shadowRef = useRef(null);
  const [phase, setPhase] = useState('idle');
  const R = size * 0.34;
  const die = R * DIE_RATIO * 2;

  const place = (pos, q) => {
    const [x, y, z] = pos;
    const s = 1 + z * 0.12;
    if (dieRef.current) dieRef.current.style.transform = `translate(${x * R}px, ${-y * R * 0.77 + z * R * 0.64}px) scale(${s})`;
    if (cubeRef.current) cubeRef.current.style.transform = qCss(q);
    if (shadowRef.current) {
      shadowRef.current.style.transform = `translate(${x * R}px, ${z * R * 0.64}px) scale(${Math.max(0.35, 1 - y * 0.6)})`;
      shadowRef.current.style.opacity = `${Math.max(0.15, 0.7 - y * 0.5)}`;
    }
  };

  const thrown = useRef(false);
  useEffect(() => {
    if (thrown.current) { thrown.current = false; return; }
    place([0, 0, 0], restQuat(value, 0));
  }, [value, size]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    diceBus.throw = (val, seed, factor = 1) => new Promise((resolve) => {
      setPhase('compress');
      sfx.compress();
      setTimeout(() => {
        setPhase('flight');
        sfx.spring(); sfx.rattle();
        const sim = simulateThrow(val, seed);
        const start = performance.now();
        let shown = 0;
        let lastHit = -1;
        const frame = (now) => {
          const time = Math.max(0, (now - start) / 1000 / factor);
          const end = time >= sim.duration;
          const st = end ? sim.frames[sim.frames.length - 1] : sampleThrow(sim, time);
          const upto = end ? sim.frames.length : Math.min(sim.frames.length, Math.round(time * 240));
          for (; shown < upto; shown++) {
            const f = sim.frames[shown];
            if (f.hit > 0.08 && f.t - lastHit > 0.05) { sfx.hit(f.hit); lastHit = f.t; }
          }
          place(st.pos, st.q);
          if (end) {
            sfx.land();
            thrown.current = true;
            setPhase('flash');
            setTimeout(() => { setPhase('idle'); }, 900 * factor);
            resolve();
            return;
          }
          requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
      }, 190 * factor);
    });
    return () => { diceBus.throw = null; };
  }, [size]); // eslint-disable-line react-hooks/exhaustive-deps

  return { dieRef, cubeRef, shadowRef, phase, die, R };
}

export function RtbDice({ x, y, size, value, team, teamColor, canRoll, onRoll, rolling }) {
  const { dieRef, cubeRef, shadowRef, phase, die } = useThrowEngine(size, value);
  const [pressed, setPressed] = useState(false);
  const col = teamColor || '#C9A84C';
  const compress = pressed || phase === 'compress';
  const tap = () => { if (!canRoll) return; setPressed(true); setTimeout(() => setPressed(false), 160); onRoll(); };
  return (
    <button type="button" data-testid="rtb-dice-dome" onClick={tap} disabled={!canRoll}
      aria-label={canRoll ? 'Pop the dice dome' : `Dice shows ${value}`}
      className={`rtb-dome2 absolute border-0 p-0 bg-transparent ${canRoll ? 'rtb-dome-ready' : ''} ${phase === 'flash' ? 'rtb-dome-flash' : ''}`}
      style={{ left: x - size / 2, top: y - size * 0.64, width: size, height: size, zIndex: Math.round(y) + 2, '--team': col }}>
      <div className="rtb-dome-side" />
      <div className="rtb-dome-base" />
      <div className="rtb-dome-floor" />
      <div className={`rtb-dome-body ${compress ? 'rtb-compress' : ''}`}>
        <div className="rtb-dome-inner">
          <div ref={shadowRef} className="rtb-die-shadow" style={{ width: die * 1.25, height: die * 0.75, marginLeft: -die * 0.62, marginTop: -die * 0.37 }} />
          <div ref={dieRef} className="rtb-die-pos" style={{ width: die, height: die, marginLeft: -die / 2, marginTop: -die }}>
            <div style={{ perspective: size * 3, width: '100%', height: '100%' }}>
              <div ref={cubeRef} data-testid="rtb-dice-value" data-value={rolling || phase === 'flight' || phase === 'compress' ? '' : value}
                className="rtb-cube2" style={{ width: die, height: die, '--h': `${die / 2}px` }}>
                {[1, 2, 3, 4, 5, 6].map((n) => <Face key={n} n={n} />)}
              </div>
            </div>
          </div>
        </div>
        <div className="rtb-dome-glass" />
      </div>
      {phase === 'flash' && <div data-testid="rtb-dice-result" className="rtb-dice-result rtb-display">ROLLED {value}</div>}
      <div className="absolute left-1/2 -translate-x-1/2 rtb-display tracking-[0.2em] text-[#F3DC8A] whitespace-nowrap" style={{ bottom: -size * 0.02, fontSize: Math.max(9, size * 0.1), textShadow: '0 0 8px #000, 0 0 6px #C9A84C' }}>
        {canRoll ? 'POP DICE DOME' : team ? `${team.toUpperCase()} DICE` : 'POP DICE'}
      </div>
    </button>
  );
}
